/**
 * Contrast rules for the ShareCard export (see share-an-insight design handoff).
 * Threshold is 3:1 (headline-scale glyphs, not body text).
 */

export type ShareCardLayout = 'stat' | 'split' | 'plaque'

/** Where, on a given layout, the customizable ink (headline + stat) is drawn. */
export const INK_GROUND: Record<ShareCardLayout, string> = {
  stat: '#003673',
  split: '#FFFFFF', // right column headline; the left-rail stat uses SPLIT_RAIL_GROUND
  plaque: '#FFFFFF',
}

/** The split layout's left rail (stat number) sits on evergreen, not white. */
export const SPLIT_RAIL_GROUND = '#0B6D5E'

const FALLBACK_FOR_GROUND: Record<string, string> = {
  '#003673': '#FFFFFF',
  '#0B6D5E': '#FFFFFF',
  '#FFFFFF': '#051E33',
}

const CONTRAST_THRESHOLD = 3

function srgbToLinear(channel: number): number {
  const c = channel / 255
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

function hexToRgb(hex: string): [number, number, number] {
  const normalized = hex.replace('#', '')
  const full =
    normalized.length === 3
      ? normalized
          .split('')
          .map((c) => c + c)
          .join('')
      : normalized
  const int = parseInt(full, 16)
  return [(int >> 16) & 255, (int >> 8) & 255, int & 255]
}

export function relativeLuminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(srgbToLinear)
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

export function contrastRatio(hexA: string, hexB: string): number {
  const lumA = relativeLuminance(hexA)
  const lumB = relativeLuminance(hexB)
  const lighter = Math.max(lumA, lumB)
  const darker = Math.min(lumA, lumB)
  return (lighter + 0.05) / (darker + 0.05)
}

export interface ResolvedInk {
  /** The color actually used for the headline + stat glyphs. */
  ink: string
  /** True when `color` failed the 3:1 threshold and `ink` is the fallback, not the pick. */
  fellBack: boolean
}

/**
 * Resolves the ink used to render the headline + stat for a given layout, falling back
 * to a legible color when the requested `color` doesn't meet the 3:1 contrast threshold
 * against that layout's ground. Never returns an illegible pairing.
 */
export function resolveInk(layout: ShareCardLayout, color: string, ground?: string): ResolvedInk {
  const effectiveGround = ground ?? INK_GROUND[layout]
  const ratio = contrastRatio(color, effectiveGround)
  if (ratio >= CONTRAST_THRESHOLD) {
    return { ink: color, fellBack: false }
  }
  const fallback = FALLBACK_FOR_GROUND[effectiveGround] ?? '#051E33'
  return { ink: fallback, fellBack: true }
}

export const CONTRAST_WARNING =
  'That color is too low-contrast on this layout, so the image falls back to a legible ink. Pick another swatch or switch layout.'
